<template>
  <div class="zo-boekhouden-manage">
    <div v-if="loading" class="zo-boekhouden-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <div
        class="zo-boekhouden-manage__banner"
        :class="
          isActive
            ? 'zo-boekhouden-manage__banner--success'
            : 'zo-boekhouden-manage__banner--info'
        "
      >
        <BaseIcon
          :name="isActive ? 'check-circle' : 'information-circle'"
          size="14"
        />
        <span>
          {{
            isActive
              ? $t('zo_boekhouden.status.active_message')
              : $t('zo_boekhouden.status.empty_message')
          }}
        </span>
      </div>

      <div class="zo-boekhouden-manage__fields">
        <TextInput
          id="zo-webhook-url"
          :label="$t('zo_boekhouden.settings.webhook_url')"
          :info="$t('zo_boekhouden.settings.webhook_url_info')"
          v-model="form.webhookUrl"
          :error="errors.webhookUrl"
          placeholder="https://…"
        />

        <TextInput
          id="zo-webhook-secret"
          type="password"
          :label="$t('zo_boekhouden.settings.webhook_secret')"
          :info="$t('zo_boekhouden.settings.webhook_secret_info')"
          v-model="form.webhookSecret"
          placeholder="••••••••"
        />

        <div class="zo-boekhouden-manage__toggle">
          <ToggleInput
            id="zo-is-active"
            :label="$t('zo_boekhouden.settings.is_active')"
            v-model="form.is_active"
          />
          <p class="zo-boekhouden-manage__toggle-hint">
            {{ $t('zo_boekhouden.settings.is_active_info') }}
          </p>
        </div>
      </div>

      <p class="zo-boekhouden-manage__help">
        {{ $t('zo_boekhouden.settings.help') }}
      </p>

      <div class="zo-boekhouden-manage__actions">
        <Btn
          v-if="integrationId"
          variant="danger"
          :loading="disconnecting"
          @click="handleDisconnect"
        >
          {{ $t('zo_boekhouden.actions.disconnect') }}
        </Btn>
        <div v-else />

        <Btn variant="primary" :loading="saving" @click="handleSave">
          {{ $t('zo_boekhouden.actions.save') }}
        </Btn>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  import { useIntegrationStore } from '@/store/integrationStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  const DEFAULT_EVENTS = ['invoice.updated', 'payment.received'];

  export default {
    name: 'ZoBoekhoudenManageModal',

    components: {
      Btn,
      BaseIcon,
      LoadingSpinner,
      TextInput,
      ToggleInput,
    },

    props: {
      modalId: { type: [String, Number], default: null },
      integrationId: { type: String, default: null },
      onUpdated: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const integrationStore = useIntegrationStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const saving = ref(false);
      const disconnecting = ref(false);
      const errors = reactive({ webhookUrl: '' });

      const form = reactive({
        webhookUrl: '',
        webhookSecret: '',
        is_active: false,
      });

      const isActive = computed(
        () => form.is_active && !!form.webhookUrl?.trim()
      );

      const populateFromIntegration = (integration) => {
        form.webhookUrl = integration?.settings?.webhookUrl || '';
        form.webhookSecret = integration?.settings?.webhookSecret || '';
        form.is_active = !!integration?.is_active;
      };

      const validate = () => {
        errors.webhookUrl = '';
        if (form.is_active && !form.webhookUrl?.trim()) {
          errors.webhookUrl = $t('zo_boekhouden.settings.webhook_url_info');
          return false;
        }
        return true;
      };

      const buildPayload = () => ({
        name: $t('zo_boekhouden.name'),
        type: 'zo_boekhouden',
        is_active: form.is_active,
        settings: {
          webhookUrl: form.webhookUrl.trim(),
          webhookSecret: form.webhookSecret,
          events: DEFAULT_EVENTS,
        },
      });

      const handleSave = async () => {
        if (!validate()) return;

        saving.value = true;
        try {
          const payload = buildPayload();
          if (props.integrationId) {
            await integrationStore.updateIntegration(props.integrationId, payload);
          } else {
            // Backend create always stores is_active=false — follow with an
            // update so the user's Active toggle is respected.
            const response = await integrationStore.createIntegration(payload, {});
            const createdId = response?.data?.id;
            if (createdId) {
              await integrationStore.updateIntegration(createdId, payload);
            }
          }

          toastStore.addToast({
            type: 'success',
            message: $t('zo_boekhouden.save.success'),
          });

          if (typeof props.onUpdated === 'function') {
            await props.onUpdated();
          }
          if (props.modalId) uiStore.closeModal(props.modalId);
        } catch (error) {
          const msg =
            error?.response?.data?.error ||
            error.message ||
            $t('zo_boekhouden.save.error');
          toastStore.addToast({ type: 'error', message: msg });
        } finally {
          saving.value = false;
        }
      };

      const handleDisconnect = () => {
        if (!props.integrationId) return;

        const confirmId = uiStore.addModal(null, {
          title: $t('zo_boekhouden.disconnect.title'),
          description: $t('zo_boekhouden.disconnect.description'),
          status: 'danger',
          submitText: $t('zo_boekhouden.disconnect.confirm'),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await integrationStore.deleteIntegration(props.integrationId);
              uiStore.closeModal(confirmId);
              if (props.modalId) uiStore.closeModal(props.modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('zo_boekhouden.disconnect.success'),
              });
              if (typeof props.onUpdated === 'function') {
                await props.onUpdated();
              }
            } catch (error) {
              uiStore.closeModal(confirmId);
              const msg =
                error?.response?.data?.error ||
                error.message ||
                $t('zo_boekhouden.disconnect.error');
              toastStore.addToast({ type: 'error', message: msg });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        if (!props.integrationId) return;

        loading.value = true;
        try {
          const hydrated = await integrationStore.ensureIntegrationDetails(
            props.integrationId,
            { force: true }
          );
          if (hydrated) populateFromIntegration(hydrated);
        } catch (error) {
          console.error('Failed to load ZO Boekhouden settings:', error);
        } finally {
          loading.value = false;
        }
      });

      return {
        loading,
        saving,
        disconnecting,
        form,
        errors,
        isActive,
        handleSave,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .zo-boekhouden-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.45;

      &--info {
        background: var(--slate-50);
        color: var(--slate-700);
      }

      &--success {
        background: var(--green-50);
        color: var(--green-800);
      }
    }

    &__fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__toggle {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__toggle-hint {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__help {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.45;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
      padding-top: 0.25rem;
    }
  }
</style>
